import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEmployeeAuth } from '../hooks/useEmployeeAuth';
import EmployeeLayout from '../components/EmployeeLayout';
import AuthShell, { authAlertSx, authButtonSx, authTextFieldSx } from '../components/AuthShell';

const EmployeeChangePassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { changePassword, loading, error, clearError, mustChangePassword } = useEmployeeAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    if (newPassword !== confirmPassword) {
      setLocalError(t('employeePortal.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setLocalError(t('employeePortal.passwordMinLength'));
      return;
    }

    if (newPassword === currentPassword) {
      setLocalError(t('employeePortal.passwordSameAsCurrent'));
      return;
    }

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      navigate('/employee/dashboard');
    } catch (err) {
      // Error handled by context
    }
  };

  const displayError = error || localError;

  const passwordForm = (
    <>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--an-radius-sm)',
              color: 'var(--an-primary)',
              backgroundColor: 'var(--an-primary-light)',
              flexShrink: 0,
            }}
          >
            <SecurityIcon />
          </Box>
          <Typography variant="h4" component="h1" fontWeight={800} sx={{ color: 'var(--an-text-primary)' }}>
            {t('employeePortal.changePasswordTitle')}
          </Typography>
        </Box>
        {mustChangePassword && (
          <Typography variant="body1" sx={{ color: 'var(--an-text-secondary)', maxWidth: 440 }}>
            {t('employeePortal.changePasswordSubtitle')}
          </Typography>
        )}
      </Box>

      {displayError && (
        <Alert severity="error" sx={authAlertSx}>
          {displayError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('employeePortal.currentPassword')}
          type={showCurrent ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrent(!showCurrent)} edge="end" sx={{ color: 'var(--an-text-secondary)' }}>
                  {showCurrent ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('employeePortal.newPassword')}
          type={showNew ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          margin="normal"
          error={newPassword !== '' && newPassword.length < 8}
          helperText={newPassword !== '' && newPassword.length < 8 ? t('employeePortal.passwordMinLength') : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew(!showNew)} edge="end" sx={{ color: 'var(--an-text-secondary)' }}>
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('employeePortal.confirmPassword')}
          type={showConfirm ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          margin="normal"
          error={confirmPassword !== '' && confirmPassword !== newPassword}
          helperText={confirmPassword !== '' && confirmPassword !== newPassword ? t('employeePortal.passwordMismatch') : ''}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" sx={{ color: 'var(--an-text-secondary)' }}>
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 1 }}
        />

        {newPassword !== '' && newPassword === currentPassword && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 700 }}>
            {t('employeePortal.passwordSameAsCurrent')}
          </Typography>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={
            loading ||
            newPassword.length < 8 ||
            newPassword !== confirmPassword ||
            newPassword === currentPassword
          }
          sx={{ ...authButtonSx, mt: 4 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('employeePortal.changePasswordButton')}
        </Button>
      </Box>
    </>
  );

  const layoutContent = (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: { xs: 2, sm: 0 }, py: { xs: 3, md: 4 } }}>
      <Card
        elevation={0}
        sx={{
          maxWidth: 540,
          width: '100%',
          borderRadius: 'var(--an-radius-md)',
          border: '1px solid var(--an-border-light)',
          boxShadow: 'var(--an-shadow-sm)',
          backgroundColor: 'var(--an-surface)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>{passwordForm}</CardContent>
      </Card>
    </Box>
  );

  return mustChangePassword ? (
    <AuthShell>{passwordForm}</AuthShell>
  ) : (
    <EmployeeLayout>{layoutContent}</EmployeeLayout>
  );
};

export default EmployeeChangePassword;
